import React, { ChangeEvent, FC, useCallback, useMemo, useRef, useState } from 'react';
import { FilterSwitch } from './FilterSwitch';
import { CUSTOM_FILTER_KEY, useFilterGroupContext } from './FilterGroupContext';
import { TYPE_WARNING } from '../notification/actions';
import { Trans, useTranslation } from 'react-i18next';
import { showNotification } from '../notification/toastNotification';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';

type FilterRangeEntryProps = {
    readonly filterKey: string;
    readonly placeholder?: string;
    readonly validation?: (value: string) => boolean;
    readonly preProcessInput?: (value: string, pos?: 'from' | 'to') => string;
    readonly postProcessInput?: (value: string, pos?: 'from' | 'to') => string;
    readonly errorMessage?: string;
    readonly variant?: 'months';
    readonly customIndex?: number;
    readonly wideInput?: boolean;
};

export const FilterRangeEntry: FC<FilterRangeEntryProps> = ({
    filterKey,
    placeholder,
    preProcessInput,
    postProcessInput,
    validation = value => /^\d*$/.test(value),
    errorMessage,
    variant,
    customIndex = 0,
    wideInput = false
}) => {
    const { t } = useTranslation();
    const { bucket, setFilter } = useFilterGroupContext(filterKey, undefined, customIndex);
    const [parsedInitialFromValue, parsedInitialToValue] = bucket?.key?.split('-') ?? [];

    const options = {
        validation,
        preProcessInput,
        postProcessInput
    };
    const fromValue = useValidationAndProcessingInput(parsedInitialFromValue, { ...options, pos: 'from' });
    const toValue = useValidationAndProcessingInput(parsedInitialToValue, { ...options, pos: 'to' });

    const filterState = bucket?.state ?? '';
    const docCount = bucket?.docCount ?? 0;

    const onFilterChange = useCallback(
        (state: 'include' | 'exclude') => {
            const fromValueCommit = fromValue.commitValue();
            const toValueCommit = toValue.commitValue();

            if (!fromValue.value && !toValue.value) {
                showNotification(
                    t('components.filterBar.filterRangeEntry.errorMessage.emptyValue'),
                    TYPE_WARNING,
                    1500
                );
            } else if (fromValueCommit && toValueCommit) {
                setFilter(state, `${fromValueCommit}-${toValueCommit}`);
            } else {
                showNotification(
                    errorMessage ?? t('components.filterBar.filterRangeEntry.errorMessage.numericEntry'),
                    TYPE_WARNING,
                    1500
                );
            }
        },
        [fromValue, toValue, setFilter, errorMessage, t]
    );

    return (
        <div className="fe">
            <div className="filter-label">
                <div className="text-label">
                    <Trans
                        i18nKey="components.filterBar.filterRangeEntry.template"
                        components={[
                            <input
                                type="text"
                                placeholder={placeholder}
                                value={fromValue.value}
                                aria-label="from"
                                onChange={fromValue.onChange}
                                className={`rangeInput ${fromValue.error ? 'error' : ''} ${wideInput ? 'wide' : ''}`}
                            />,
                            <input
                                type="text"
                                placeholder={placeholder}
                                value={toValue.value}
                                aria-label="to"
                                onChange={toValue.onChange}
                                className={`rangeInput ${toValue.error ? 'error' : ''} ${wideInput ? 'wide' : ''}`}
                            />
                        ]}
                        context={variant}
                    />
                    &nbsp;
                    <span className="count" aria-label="doc-count">
                        ({docCount})
                    </span>
                    {!!filterState && (fromValue.hasChanged || toValue.hasChanged) ? (
                        <a onClick={() => onFilterChange(filterState)} title="update range" aria-label="update-range">
                            <i className="material-icons md-upper">done</i>
                        </a>
                    ) : null}
                </div>
                <FilterSwitch onFilterChange={onFilterChange} filterState={filterState} />
            </div>
        </div>
    );
};

type useValidationAndProcessingInputOptions = {
    readonly validation?: (value: string) => boolean;
    readonly preProcessInput?: (value: string, pos?: 'from' | 'to') => string;
    readonly postProcessInput?: (value: string, pos?: 'from' | 'to') => string;
    readonly pos?: 'from' | 'to';
};

const useValidationAndProcessingInput = (
    initialValue: string | undefined = '',
    options: useValidationAndProcessingInputOptions = {}
) => {
    const [internalValue, setInternalValue] = useState('');
    const setCounter = useRef(0);

    const setValueInternal = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setCounter.current++;
        setInternalValue(event.target.value);
    }, []);

    const preProcessedValue = useMemo(() => {
        return options.preProcessInput ? options.preProcessInput(initialValue, options.pos) : initialValue;
    }, [initialValue, options]);

    const consideredValue = useMemo((): string => {
        return setCounter.current === 0 ? preProcessedValue : internalValue;
    }, [preProcessedValue, internalValue]);

    const error = useMemo((): boolean => {
        return !!options.validation && consideredValue.length > 0 && !options.validation(consideredValue);
    }, [consideredValue, options]);

    const postProcessedValue = useMemo(() => {
        if (error) {
            return undefined;
        }

        return options.postProcessInput ? options.postProcessInput(consideredValue, options.pos) : consideredValue;
    }, [options, consideredValue, error]);

    const commitValue = useCallback((): string | undefined => {
        if (error) {
            return undefined;
        }

        setCounter.current = 0;
        return postProcessedValue;
    }, [postProcessedValue, error]);

    return {
        value: consideredValue,
        onChange: setValueInternal,
        commitValue,
        error,
        hasChanged: initialValue !== postProcessedValue
    };
};

type CurrencyFilterRangeEntryProps = {
    readonly filterKey: string;
};

export const CurrencyFilterRangeEntry: FC<CurrencyFilterRangeEntryProps> = ({ filterKey }) => {
    const {
        spoc: { currency }
    } = useAuthentication();

    const validation = useCallback((value: string) => /^\d{1,6}(\.\d{1,2})?$/.test(value), []);
    const { t } = useTranslation();
    return (
        <FilterRangeEntry
            filterKey={filterKey}
            placeholder={currency}
            validation={validation}
            errorMessage={t('components.filterBar.filterRangeEntry.errorMessage.currencyEntry')}
            preProcessInput={preProcessCurrencyFilter}
            postProcessInput={postProcessCurrencyFilter}
        />
    );
};

export const MonthsFilterRangeEntry: FC<{ customIndex: number }> = ({ customIndex }) => {
    const { t } = useTranslation();
    return (
        <FilterRangeEntry
            filterKey={CUSTOM_FILTER_KEY}
            placeholder={t('components.filterBar.filterRangeEntry.placeholder.months')}
            validation={value => /^\d*$/.test(value)}
            preProcessInput={preProcessMonthsFilter}
            postProcessInput={postProcessMonthsFilter}
            customIndex={customIndex}
            variant="months"
        />
    );
};

export const DateFilterRangeEntry: FC<{ customIndex: number }> = ({ customIndex }) => {
    const { t } = useTranslation();
    return (
        <FilterRangeEntry
            filterKey={CUSTOM_FILTER_KEY}
            placeholder={t('components.filterBar.filterRangeEntry.placeholder.date')}
            validation={value => /^\d{2}\/\d{2}\/\d{4}$|^$/.test(value)}
            errorMessage={t('components.filterBar.filterRangeEntry.errorMessage.dateEntry')}
            preProcessInput={preProcessDateFilter}
            postProcessInput={postProcessDateFilter}
            customIndex={customIndex}
            wideInput={true}
        />
    );
};

type IntegerFilterRangeEntryProps = {
    readonly filterKey: string;
    readonly placeholder: string;
};

export const IntegerFilterRangeEntry: FC<IntegerFilterRangeEntryProps> = ({ filterKey, placeholder }) => {
    return (
        <FilterRangeEntry
            filterKey={filterKey}
            placeholder={placeholder}
            preProcessInput={preProcessNumberFilter}
            postProcessInput={postProcessIntegerFilter}
        />
    );
};

type FloatingPointFilterRangeEntryProps = {
    readonly filterKey: string;
    readonly placeholder: string;
};

export const FloatingPointFilterRangeEntry: FC<FloatingPointFilterRangeEntryProps> = ({ filterKey, placeholder }) => {
    const validation = useCallback((value: string) => /^\d{1,6}(\.\d{1,4})?$/.test(value), []);

    return (
        <FilterRangeEntry
            filterKey={filterKey}
            placeholder={placeholder}
            preProcessInput={preProcessNumberFilter}
            postProcessInput={postProcessFloatingPointFilter}
            validation={validation}
        />
    );
};

export function postProcessFloatingPointFilter(e: string, pos?: 'from' | 'to') {
    let fallback = '';
    switch (pos) {
        case 'from':
            fallback = '0';
            break;
        case 'to':
            fallback = '*';
            break;
    }

    return e.length > 0 ? e : fallback;
}

export function preProcessNumberFilter(e: string, pos?: 'from' | 'to'): string {
    switch (pos) {
        case 'from':
            return e.replace(/\*/g, '0');
        case 'to':
            return e.replace(/\*/g, '');
        default:
            return e;
    }
}

export function postProcessIntegerFilter(e: string, pos?: 'from' | 'to'): string {
    const num = parseInt(e, 10);
    if (isNaN(num)) {
        switch (pos) {
            case 'from':
                return '0';
            case 'to':
                return '*';
            default:
                return '';
        }
    }

    return Math.floor(num).toString();
}

export function postProcessCurrencyFilter(e: string, pos?: 'from' | 'to'): string {
    const num = parseFloat(e.replace(/,/g, ''));
    if (isNaN(num)) {
        switch (pos) {
            case 'from':
                return '0';
            case 'to':
                return '*';
            default:
                return '';
        }
    }

    return Math.floor(num * 100).toString();
}

export function preProcessCurrencyFilter(e: string): string {
    const num = parseInt(e, 10);
    if (isNaN(num)) {
        return '';
    }

    return (num / 100).toString();
}

export function preProcessMonthsFilter(e: string): string {
    return e.replace(/M/g, '').replace(/\*/g, '');
}

export function postProcessMonthsFilter(e: string): string {
    return e.length > 0 ? `${e}M` : '*';
}

export function preProcessDateFilter(e: string): string {
    return e === '*' ? '' : e;
}

export function postProcessDateFilter(e: string): string {
    return e.length > 0 ? `${e}` : '*';
}
